// Book cover thumbnails, generated from the local file (covers aren't synced).
//  - EPUB: the embedded cover image (epub.js `coverUrl()`), downscaled.
//  - PDF: the first page rendered to a small canvas.
// Results are cached as data URLs in IndexedDB so the grid doesn't re-render on
// every load. Books that aren't available locally (or have no cover) return null
// and the UI shows a styled placeholder.

// pdf.js and epub.js are heavy; import them lazily so they stay out of the main
// bundle (the landing/library pages shouldn't pay for them up front).
import { resolveBookBlob } from './library';
import { getCoverRec, putCoverRec } from './db';

const THUMB_W = 240;

export interface CoverBook {
  id: string;
  title: string;
  type: 'PDF' | 'EPUB';
}

export async function getCover(book: CoverBook): Promise<string | null> {
  const cached = await getCoverRec(book.id).catch(() => undefined);
  if (cached !== undefined) return cached || null; // '' = known to have no cover

  const blob = await resolveBookBlob(book.id, book.title).catch(() => null);
  if (!blob) return null; // not local yet — try again once it's downloaded

  let dataUrl = '';
  try {
    dataUrl = book.type === 'EPUB' ? await epubCover(blob) : await pdfCover(blob);
  } catch {
    dataUrl = '';
  }
  await putCoverRec(book.id, dataUrl).catch(() => {});
  return dataUrl || null;
}

async function epubCover(blob: Blob): Promise<string> {
  const { default: ePub } = await import('epubjs');
  const buf = await blob.arrayBuffer();
  const book = ePub(buf as ArrayBuffer);
  try {
    await book.ready;
    const url = await book.coverUrl();
    if (!url) return '';
    const thumb = await imageUrlToThumb(url, THUMB_W);
    try {
      URL.revokeObjectURL(url);
    } catch {
      // not an object URL
    }
    return thumb;
  } finally {
    try {
      book.destroy();
    } catch {
      // ignore
    }
  }
}

async function pdfCover(blob: Blob): Promise<string> {
  const pdfjsLib = await import('pdfjs-dist');
  const { default: workerUrl } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  const data = new Uint8Array(await blob.arrayBuffer());
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  try {
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: THUMB_W / base.width });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d')!;
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas.toDataURL('image/jpeg', 0.8);
  } finally {
    try {
      pdf.destroy();
    } catch {
      // ignore
    }
  }
}

function imageUrlToThumb(url: string, maxW: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxW / (img.width || maxW));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      c.getContext('2d')!.drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => reject(new Error('cover image failed to load'));
    img.src = url;
  });
}
